import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.bubble.css';
import '../../../page-builder/components/text-editor/style.scss';
import Quill, { Parchment } from 'quill';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StringinterpolationOptions } from '../../../page-builder/interface/commonInterface';
import { options } from '../../../page-builder/utils/constants';
import strikethroughIcon from '../../../assets/svg/strikethroughIcon';

type ToolbarModule = {
  addHandler: (_format: string, _handler: (_value: string) => void) => void;
};
interface QuillToolbarModule {
  container: HTMLElement;
}

// Font and Size configurations (unchanged)
const fonts = [
  'shopify-dawn-stack',
  'debut-stack',
  'brooklyn-stack',
  'narrative-stack',
  'infinity-mystic-stack',
  'infinity-radiant-stack',
  'combine-krown-themes-stack',
  'kingdom-krown-themes-stack',
  'inter-stack',
  'system-ui-stack',
  'full-system-ui-emoji-stack',
  'clean-sans-stack',
  'classic-serif-stack',
  'modern-sans-stack',
  'rounded-stack',
  'neutral-stack',
  'editorial-serif-stack',
  'code-mono-stack',
  'open-sans-stack',
  'apple-system',
  'system-ui',
  'Inter',
  'sans-serif',
  'Lato',
  'Roboto',
  'Helvetica',
  'Georgia',
  'Futura',
  'Poppins',
  'Pacifico',
  'LobsterTwo',
  'Cabin',
  'Playball',
  'Abel',
  'AbrilFatface',
  'Alegreya',
  'AlegreyaSans',
  'Aleo',
  'Amiri',
  'AnonymousPro',
  'Archivo',
  'ArchivoNarrow',
  'Arimo',
  'Arsenal',
  'Arvo',
  'Assistant',
  'Asul',
  'Bevan',
  'Bitter',
  'BodoniModarn',
  'BricolageGrotesque',
  'Cardo',
  'Catamaran',
  'Chivo',
  'Cormorant',
  'CourierNew',
  'CrimsonText',
  'DMSans',
  'Domine',
  'Dosis',
  'EBGaramond',
  'Eczar',
  'Fahkwang',
  'Figtree',
  'FiraSans',
  'FjallaOne',
  'Fraunces',
  'Gluten',
  'Grandstander',
  'HeptaSlab',
  'IBMPlexSans',
  'Inconsolata',
  'InknutAntiqua',
  'InstrumentSans',
  'JosefinSans',
  'JosefinSlab',
  'Jost',
  'Kalam',
  'Karla',
  'KaushanScript',
  'KleeOne',
  'Kreon',
  'LibreBodoni',
  'LibreCaslonText',
  'LibreFranklin',
  'Lora',
  'LucidaGrande',
  'Manuale',
  'MavenPro',
  'Montserrat',
  'Muli',
  'Neuton',
  'Newsreader',
  'Nobile',
  'NotoSerif',
  'Nunito',
  'NunitoSans',
  'OldStandardTT',
  'OpenSans',
  'Oswald',
  'Ovo',
  'Palatino',
  'Petrona',
  'PinyonScript',
  'Platypi',
  'Playfair',
  'PlaypenSans',
  'Prompt',
  'ProzaLibre',
  'PTSans',
  'PTSerif',
  'Quantico',
  'QuattrocentoSans',
  'Questrial',
  'Quicksand',
  'Rajdhani',
  'Raleway',
  'Rasa',
  'Roboto',
  'RobotoBlack',
  'RobotoCondensed',
  'RobotoMono',
  'RobotoSlab',
  'Rubik',
  'Saira',
  'ShadowsIntoLight',
  'Smooch',
  'SourceCodePro',
  'SourceSansPro',
  'SpaceMono',
  'Syne',
  'TenorSans',
  'TitilliumWeb',
  'Tomorrow',
  'TrebuchetMS',
  'Trirong',
  'Ubuntu',
  'UnicaOne',
  'UnifrakturCook',
  'UnifrakturMaguntia',
  'WorkSans',
  'Yrsa',
  'ZillaSlab',
  'BIZUDPMincho',
  'DelaGothicOne',
  'HinaMincho',
  'KaiseiOpti',
  'KaiseiTokumin',
  'MPLUS',
  'MPLUSRounded1c',
  'MochiyPopPOne',
  'Murecho',
  'NotoSansJapanese',
  'NotoSerifJapanese',
  'ZenKakuNew',
  'ZenKurenaido',
  'ZenMaruGothic',
  'ZenOldMincho',
  'GillSans',
  'Frutiger',
  'MyriadPro',
  'Optima',
  'SegoeUI',
  'Verdana',
  'lucidasans',
  'calibri',
  'NotoSans',
  'BlinkMacSystem',
  'SanFrancisco',
  'HelveticaNeue',
  'SFPro',
  'SFCompact',
  'SFMono',
  'NewYork',
];
const fontSizes = Array.from({ length: 100 }, (_, i) => `${i + 1}px`);

export type QuillTextEditorProps = {
  content: string;
};

const Font = Quill.import('formats/font') as Parchment.Attributor;
Font.whitelist = fonts;
Quill.register(Font, true);

const lineHeightConfig = { scope: Parchment.Scope.BLOCK };
const lineHeightClass = new Parchment.ClassAttributor(
  'lineheight',
  'ql-line-height',
  lineHeightConfig,
);
const lineHeightStyle = new Parchment.StyleAttributor(
  'lineheight',
  'line-height',
  lineHeightConfig,
);
const fontWeightConfig = { scope: Parchment.Scope.INLINE };
const fontWeightClass = new Parchment.ClassAttributor(
  'fontweight',
  'ql-font-weight',
  fontWeightConfig,
);
const fontWeightStyle = new Parchment.StyleAttributor(
  'fontweight',
  'font-weight',
  fontWeightConfig,
);
const StrikeColorStyle = new Parchment.StyleAttributor(
  'strikeColor',
  'text-decoration-color',
  {
    scope: Parchment.Scope.INLINE,
  },
);
const LetterSpacingStyle = new Parchment.StyleAttributor(
  'letterspacing',
  'letter-spacing',
  { scope: Parchment.Scope.INLINE, whitelist: ['0px', ...fontSizes] },
);
const Size = new Parchment.StyleAttributor('size', 'font-size', {
  scope: Parchment.Scope.INLINE,
});
Size.whitelist = fontSizes;
Quill.register(Size, true);
Quill.register(StrikeColorStyle, true);
Quill.register(lineHeightClass);
Quill.register(lineHeightStyle);
Quill.register(fontWeightClass);
Quill.register(fontWeightStyle);
Quill.register(LetterSpacingStyle);

const formats = [
  'header',
  'list',
  'bullet',
  'bold',
  'italic',
  'underline',
  'color',
  'background',
  'link',
  'align',
  'font',
  'size',
  'script',
  'clean',
  'check',
  'small',
  'large',
  'huge',
  'super',
  'sub',
  'code-block',
  'lineheight',
  'fontweight',
  'strike',
  'strikeColor',
  'letterspacing',
];

type QuillEditorProps = {
  value: string;
  onChange: (_value: string) => void;
  enableLink?: boolean;
  enableColor?: boolean; // Optional prop to toggle color button
  id?: number;
  isDisableEditor?: boolean; // Optional prop to disable the editor
};
export const QuillEditor = forwardRef<ReactQuill, QuillEditorProps>(
  ({
    value,
    onChange,
    enableLink = true,
    enableColor = true,
    id,
    isDisableEditor = false,
  }) => {
    const quillRef = useRef<ReactQuill | null>(null);
    const colorPickerRef = useRef<HTMLInputElement | null>(null);
    const backgroundColorPickerRef = useRef<HTMLInputElement | null>(null);
    const colorStrikePickerRef = useRef<HTMLInputElement | null>(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
    const [cursorPosition, setCursorPosition] = useState<number>(0);
    const [searchParam] = useSearchParams();
    const [filteredOptions, setFilteredOptions] = useState<
      StringinterpolationOptions[]
    >([]);

    const updatedOptions = React.useMemo((): StringinterpolationOptions[] => {
      return options.filter(option => {
        return (
          !option.showInTemplates ||
          option.showInTemplates.length === 0 ||
          option.showInTemplates.includes(searchParam.get('template') || '')
        );
      });
    }, [searchParam]);

    const applyColorFormat = (
      quillInstance: Quill,
      type: string,
      value: string,
    ) => {
      try {
        const range = quillInstance.getSelection();

        if (range && range.length > 0) {
          // If text is selected, format only that selection
          quillInstance.formatText(range.index, range.length, type, value);
        } else {
          // For whole document, when no text is selected
          const length = quillInstance.getLength();

          // Save cursor position
          const currentPosition = range ? range.index : 0;

          // Select all text
          quillInstance.setSelection(0, length);

          // Apply format
          quillInstance.format(type, value);

          // Restore cursor position
          quillInstance.setSelection(currentPosition, 0);
        }
      } catch (error) {
        console.error(`Failed to apply ${type} format:`, error);
      }
    };

    const updateColorPickerValues = (quillInstance: Quill) => {
      const selection = quillInstance.getSelection();
      const format = selection
        ? quillInstance.getFormat(selection.index, selection.length)
        : quillInstance.getFormat(0);

      const textColor = format.color || '#000000';
      const bgColor = format.background || '#ffffff';

      if (colorPickerRef.current) {
        colorPickerRef.current.value = textColor.toString();
      }
      if (backgroundColorPickerRef.current) {
        backgroundColorPickerRef.current.value = bgColor.toString();
      }

      // Update toolbar button colors and underline
      const toolbar = quillInstance.getModule('toolbar') as QuillToolbarModule;
      if (toolbar) {
        const colorButton = toolbar.container.querySelector('.ql-color');
        const backgroundButton =
          toolbar.container.querySelector('.ql-background');

        if (colorButton) {
          if (!(textColor === 'rgb(0, 0, 0)' || textColor === '#000000')) {
            colorButton
              ?.querySelector('.ql-picker-label')
              ?.classList.add('ql-active');
            const line = colorButton?.querySelector('line');
            if (line) {
              line.style.stroke = textColor.toString();
            }
          }

          const colorPickerLabel =
            colorButton.querySelector('.ql-picker-label');
          if (colorPickerLabel) {
            (colorPickerLabel as HTMLElement).style.color =
              textColor.toString();
            (colorPickerLabel as HTMLElement).style.borderBottom =
              `2px solid ${textColor}`; // Add underline
          }
        }
        if (backgroundButton) {
          if (!(bgColor === 'rgb(255, 255, 255)' || bgColor === '#ffffff')) {
            backgroundButton
              ?.querySelector('.ql-picker-label')
              ?.classList.add('ql-active');

            const tag = backgroundButton?.querySelector('g');
            if (tag) {
              tag.style.fill = bgColor.toString();
            }
          }

          const bgPickerLabel =
            backgroundButton.querySelector('.ql-picker-label');
          if (bgPickerLabel) {
            (bgPickerLabel as HTMLElement).style.color = bgColor.toString();
            (bgPickerLabel as HTMLElement).style.borderBottom =
              `2px solid ${bgColor}`; // Add underline
          }
        }
      }
    };

    const setupColorPickers = (quillInstance: Quill) => {
      const toolbar = quillInstance.getModule('toolbar') as QuillToolbarModule;
      if (!toolbar) return;

      const colorButton = toolbar.container.querySelector('.ql-color');
      const backgroundButton =
        toolbar.container.querySelector('.ql-background');

      const strikeColorButton =
        toolbar.container.querySelector('.ql-strikeColor');

      const colorPickerHandler = (
        event: Event,
        type: 'color' | 'background' | 'strikeColor',
      ) => {
        event.preventDefault();
        event.stopPropagation();
        const pickerRef =
          type === 'color'
            ? colorPickerRef
            : type === 'background'
              ? backgroundColorPickerRef
              : colorStrikePickerRef;

        if (pickerRef.current) {
          const range = quillInstance.getSelection();
          pickerRef.current.click();

          pickerRef.current.oninput = (e: Event) => {
            const value = (e.target as HTMLInputElement).value;
            if (value) {
              if (range) {
                quillInstance.setSelection(range.index, range.length);
              }
              applyColorFormat(quillInstance, type, value);
              updateColorPickerValues(quillInstance);
              quillInstance.focus();
            }
          };
        }
      };

      // Store references to the wrapped handlers for removal
      const colorHandler = (e: Event) => colorPickerHandler(e, 'color');
      const backgroundHandler = (e: Event) =>
        colorPickerHandler(e, 'background');
      const strikeColorHandler = (e: Event) =>
        colorPickerHandler(e, 'strikeColor');

      if (colorButton && enableColor) {
        colorButton.removeAttribute('data-value');
        colorButton.addEventListener('mousedown', colorHandler, {
          capture: true,
        });
      }
      if (backgroundButton && enableColor) {
        backgroundButton.addEventListener('mousedown', backgroundHandler, {
          capture: true,
        });
      }
      if (strikeColorButton && enableColor) {
        strikeColorButton.addEventListener('mousedown', strikeColorHandler, {
          capture: true,
        });
      }

      quillInstance.on('selection-change', () => {
        updateColorPickerValues(quillInstance);
      });

      quillInstance.on('text-change', () => {
        updateColorPickerValues(quillInstance);
      });

      return () => {
        if (colorButton)
          colorButton.removeEventListener('mousedown', colorHandler, {
            capture: true,
          });
        if (backgroundButton)
          backgroundButton.removeEventListener('mousedown', backgroundHandler, {
            capture: true,
          });
        if (strikeColorButton)
          strikeColorButton.removeEventListener(
            'mousedown',
            strikeColorHandler,
            { capture: true },
          );
      };
    };

    const handleEditorReady = () => {
      if (quillRef.current) {
        const quillInstance = quillRef.current.getEditor();
        if (enableLink) {
          const saveButton = document.querySelector('.ql-action');
          if (saveButton) {
            saveButton.addEventListener('click', () => {
              const selection = quillInstance.getSelection();
              if (selection && selection.length > 0) {
                const range = selection.index;
                setTimeout(() => {
                  const currentFormat = quillInstance.getFormat(
                    range,
                    range + selection.length,
                  );
                  if (!currentFormat['underline']) {
                    quillInstance.format('underline', true);
                  }

                  const color = quillInstance.getFormat(range).color;
                  if (color) {
                    quillInstance.format('color', color);
                  }
                }, 0);
              }
            });
          }
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<Element>) => {
      if (showDropdown) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightedIndex(prev => (prev + 1) % filteredOptions.length);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlightedIndex(
            prev =>
              (prev - 1 + filteredOptions.length) % filteredOptions.length,
          );
        } else if (e.key === 'Enter') {
          e.preventDefault();
          const selectedOption = filteredOptions[highlightedIndex];
          handleSelectOption(selectedOption?.key);
          setHighlightedIndex(0);
        } else if (e.key === 'Escape') {
          setShowDropdown(false);
        }
      }
    };

    // Utility function to filter options by token (case-insensitive)
    const filterOptionsByToken = (
      token: string,
      allOptions: StringinterpolationOptions[],
    ): StringinterpolationOptions[] => {
      const lowerToken = token.toLowerCase();
      return allOptions.filter(
        option =>
          option.label.toLowerCase().includes(lowerToken) ||
          option.key.toLowerCase().includes(lowerToken),
      );
    };

    const handleCursorCheck = () => {
      const editor = quillRef?.current?.getEditor();
      const selection = editor?.getSelection();
      const text = editor?.getText() || '';

      // Ensure we have a valid cursor position
      if ((selection && selection.index !== undefined) || cursorPosition) {
        const cursorPositionValue = selection?.index
          ? selection?.index
          : cursorPosition;
        setCursorPosition(cursorPositionValue);

        // Find the last '{' before the cursor
        const lastBraceIndex = text.lastIndexOf('{', cursorPositionValue - 1);

        // If there's no '{' found before the cursor, hide dropdown.
        if (lastBraceIndex === -1) {
          setShowDropdown(false);
          setFilteredOptions([]);
          return;
        }

        // Extract token: characters after '{' up to the cursor position.
        const token = text.substring(lastBraceIndex + 1, cursorPositionValue);

        // When token is empty, show all available options.
        const optionsToFilter = updatedOptions;
        const filtered =
          token.length > 0
            ? filterOptionsByToken(token, optionsToFilter)
            : optionsToFilter;

        // Show dropdown only if there are matching options.
        if (filtered.length > 0) {
          setFilteredOptions(filtered);
          setShowDropdown(true);
        } else {
          setShowDropdown(false);
          setFilteredOptions([]);
        }
      } else {
        setShowDropdown(false);
        setFilteredOptions([]);
      }
    };

    const handleTextChange = (content: string) => {
      if (!quillRef.current) return;
      const quill = quillRef.current.getEditor();

      // Compare using Delta instead of raw HTML to prevent infinite loop
      const currentDelta = quill.getContents();
      const newDelta = quill.clipboard.convert({ html: content });

      const deltaChanged =
        JSON.stringify(currentDelta.ops) !== JSON.stringify(newDelta.ops);

      if (deltaChanged) {
        onChange(content);
      }
      handleCursorCheck();
    };

    // Instead of deleting from `{` to cursor, we only delete what the user typed after `{`.
    const handleSelectOption = (key: string) => {
      const editor = quillRef.current?.getEditor();
      if (!editor || !key) return;

      editor.focus();
      const range = editor.getSelection(true);
      if (!range) return;
      const cursorPos = range.index;
      const text = editor.getText();
      const lastBraceIndex = text.lastIndexOf('{', cursorPos - 1);
      if (lastBraceIndex === -1) {
        setShowDropdown(false);
        return;
      }
      // Get formats applied to the '{'
      const braceFormats = { ...editor.getFormat(lastBraceIndex, 1) };
      // Delete everything after '{' up to cursor
      editor.deleteText(lastBraceIndex + 1, cursorPos - (lastBraceIndex + 1));

      const insertPos = lastBraceIndex + 1;
      const toInsert = `${key}}`;

      const maxLen = editor.getLength();
      if (insertPos > maxLen) return;

      editor.insertText(insertPos, toInsert);
      editor.formatText(insertPos, toInsert.length, braceFormats);
      editor.setSelection(insertPos + toInsert.length, 0);
      setShowDropdown(false);
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const applyFormatSmartly = (quill: Quill, format: string, value: any) => {
      const range = quill.getSelection();
      const length = quill.getLength();

      if (range && range.length > 0) {
        quill.format(format, value);
      } else {
        quill.formatText(0, length - 1, format, value); // -1 to avoid final new line
      }
    };

    useEffect(() => {
      if (quillRef.current) {
        const quillInstance = quillRef.current.getEditor();
        const toolbar = quillInstance.getModule('toolbar') as ToolbarModule;

        if (toolbar) {
          toolbar.addHandler('size', (value: string) => {
            applyFormatSmartly(quillInstance, 'size', value);
          });
          toolbar.addHandler('font', (value: string) => {
            applyFormatSmartly(quillInstance, 'font', value);
          });
          toolbar.addHandler('fontweight', (value: string) => {
            applyFormatSmartly(quillInstance, 'fontweight', value);
          });
          toolbar.addHandler('lineheight', (value: string) => {
            applyFormatSmartly(quillInstance, 'lineheight', value);
          });
          toolbar.addHandler('bold', (value: string) => {
            applyFormatSmartly(quillInstance, 'bold', value);
          });
          toolbar.addHandler('italic', (value: string) => {
            applyFormatSmartly(quillInstance, 'italic', value);
          });
          toolbar.addHandler('underline', (value: string) => {
            applyFormatSmartly(quillInstance, 'underline', value);
          });
          toolbar.addHandler('strike', (value: string) => {
            applyFormatSmartly(quillInstance, 'strike', value);
          });
          // Add this to your toolbar handlers
          toolbar.addHandler('clean', () => {
            const range = quillInstance.getSelection();
            const length = quillInstance.getLength();

            if (range && range.length > 0) {
              // If text is selected, only clean that selection
              quillInstance.removeFormat(range.index, range.length);
            } else {
              // For whole document, when no text is selected
              // Save cursor position
              const currentPosition = range ? range.index : 0;

              // Select all text
              quillInstance.setSelection(0, length - 1);

              // Apply clean (remove all formatting)
              quillInstance.removeFormat(0, length - 1);

              // Restore cursor position
              quillInstance.setSelection(currentPosition, 0);
            }
            // Update UI
            updateColorPickerValues(quillInstance);
          });
          toolbar.addHandler('letterspacing', (value: string) => {
            applyFormatSmartly(quillInstance, 'letterspacing', value);
          });
        }
        const cleanupColorPickers = setupColorPickers(quillInstance);
        handleEditorReady();

        updateColorPickerValues(quillInstance);

        setTimeout(() => {
          quillInstance.focus();
          quillInstance.setSelection(0, 0);
        }, 10);

        return () => {
          cleanupColorPickers?.();
        };
      }
    }, [enableLink, enableColor]);

    useEffect(() => {
      const button = document.getElementsByClassName('ql-strikeColor');
      if (button.length) {
        button[0].innerHTML = strikethroughIcon;
      }
    }, []);

    function normalizeText(text: string) {
      return text.normalize('NFKC');
    }

    useEffect(() => {
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        const editorElement = quill.root;

        const pasteHandler = (e: ClipboardEvent) => {
          e.preventDefault();
          e.stopPropagation();

          const clipboardData = e.clipboardData;
          if (!clipboardData) {
            return;
          }

          const pastedHtml =
            clipboardData.getData('text/html') ||
            clipboardData.getData('text/plain');

          const parser = new DOMParser();
          const doc = parser.parseFromString(pastedHtml, 'text/html');

          const trimmedText = (doc.body.textContent || '').trim();

          const cleanText = normalizeText(trimmedText);

          const range = quill.getSelection(true);
          if (range) {
            quill.deleteText(range.index, range.length, 'user');
            quill.insertText(range.index, cleanText, 'user');
            quill.setSelection(range.index + cleanText.length, 0, 'user');
          }
        };

        editorElement.addEventListener('paste', pasteHandler, {
          capture: true,
        });

        return () => {
          editorElement.removeEventListener('paste', pasteHandler, {
            capture: true,
          });
        };
      }
    }, [quillRef]);

    useEffect(() => {
      const button = document.getElementsByClassName('ql-strikeColor');
      if (button.length) {
        button[0].innerHTML = strikethroughIcon;
      }
    }, []);

    const modules = {
      toolbar: `#toolbar-${id}`,
    };

    return (
      <div className="relative mt-2">
        <div id={`toolbar-${id}`}>
          {!isDisableEditor && (
            <>
              {' '}
              {/* Group 1: Header dropdown and text formatting */}
              <span className="ql-formats">
                <select
                  className="ql-header"
                  style={{
                    color: 'white',
                  }}>
                  <option value="1">Heading 1</option>
                  <option value="2">Heading 2</option>
                  <option value="3">Heading 3</option>
                  <option value="" selected>
                    Normal
                  </option>
                </select>
                <button className="ql-bold" />
                <button className="ql-italic" />
                <button className="ql-underline" />
                <button className="ql-strike" />
                <button className="ql-strikeColor" id="strikecolor" />
                <input
                  type="color"
                  ref={colorStrikePickerRef}
                  style={{ visibility: 'hidden' }}
                  className="absolute top-10 left-0"
                />
              </span>
              {/* Group 2: Color, background, custom strikeColor, list options, clean and link */}
              <span className="ql-formats">
                <select className="ql-color" />
                <input
                  type="color"
                  ref={colorPickerRef}
                  style={{ visibility: 'hidden' }}
                  className="absolute top-10 left-0"
                />
                <select className="ql-background" />
                <input
                  type="color"
                  ref={backgroundColorPickerRef}
                  style={{ visibility: 'hidden' }}
                  className="absolute top-10 left-0"
                />
                <button className="ql-list" value="ordered" />
                <button className="ql-list" value="bullet" />
                <button className="ql-clean" />
                {/* Include the link button; if you want it conditional, you can render this button based on your enableLink flag */}
                <button className="ql-link" />
              </span>
              {/* Group 3: Font and align */}
              <span className="ql-formats">
                <select
                  className="ql-font"
                  style={{
                    color: 'white',
                  }}>
                  {fonts.map((font, index) => (
                    <option
                      key={index}
                      value={font}
                      style={{
                        color: 'white',
                      }}>
                      {font}
                    </option>
                  ))}
                </select>
                <select className="ql-align" />
              </span>
              {/* Group 4: Code block */}
              <span className="ql-formats">
                <button className="ql-code-block" />
              </span>
              {/* Group 5: Font weight (custom) */}
              <span className="ql-formats">
                <select
                  className="ql-size"
                  style={{
                    color: 'white',
                  }}>
                  {fontSizes.map((size, index) => (
                    <option
                      key={index}
                      value={size}
                      style={{
                        color: 'white',
                      }}>
                      {size}
                    </option>
                  ))}
                </select>
              </span>
              <span className="ql-formats">
                <select
                  className="ql-fontweight"
                  style={{
                    color: 'white',
                  }}>
                  {/* Replace these options with those from your fontWeightOp array */}
                  <option value="100" selected>
                    100
                  </option>
                  <option value="200">200</option>
                  <option value="300">300</option>
                  <option value="400">400</option>
                  <option value="500">500</option>
                  <option value="600">600</option>
                  <option value="700">700</option>
                  <option value="800">800</option>
                  <option value="900">900</option>
                </select>
              </span>
              {/* Group 6: Line height */}
              <span className="ql-formats">
                <select
                  className="ql-lineheight"
                  style={{
                    color: 'white',
                  }}>
                  <option value="">Normal</option>
                  <option value="0.5">0.5</option>
                  <option value="0.6">0.6</option>
                  <option value="0.7">0.7</option>
                  <option value="0.8">0.8</option>
                  <option value="0.9">0.9</option>
                  <option value="1">1</option>
                  <option value="1.25">1.25</option>
                  <option value="1.5">1.5</option>
                  <option value="1.75">1.75</option>
                  <option value="2">2</option>
                  <option value="2.5">2.5</option>
                  <option value="3">3</option>
                  <option value="3.5">3.5</option>
                  <option value="4">4</option>
                  <option value="4.5">4.5</option>
                  <option value="5">5</option>
                </select>
              </span>
            </>
          )}
          {/* Group 7: Letter Space dropdown */}
          <span className="ql-formats">
            <select
              className="ql-letterspacing"
              style={{
                color: 'white',
              }}>
              {['0px', ...fontSizes]?.map((size, index) => (
                <option
                  key={index}
                  value={size}
                  style={{
                    color: 'white',
                  }}>
                  {size}
                </option>
              ))}
            </select>
          </span>
          {/* Group 8: Subscript and superscript */}
          <span className="ql-formats">
            <button className="ql-script" value="sub" />
            <button className="ql-script" value="super" />
          </span>
        </div>
        <ReactQuill
          ref={quillRef} // Store ReactQuill instance reference
          className="pagebuilder-theme fonts-only"
          theme="snow"
          onChange={handleTextChange}
          value={value}
          modules={modules}
          formats={formats}
          placeholder="create content here..."
          onKeyDown={handleKeyDown}
        />
        {showDropdown && (
          <ul
            className="pagebuilder-theme pop-over-list"
            style={{
              position: 'absolute',
              background: 'black',
              color: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px',
              zIndex: 1000,
              padding: '0',
              margin: '4px 0',
              listStyle: 'none',
              width: '200px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            }}>
            {filteredOptions?.map((option, index) => (
              <li
                key={option.key}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelectOption(option.key);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                style={{
                  padding: '8px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: '14px',
                  backgroundColor:
                    highlightedIndex === index
                      ? 'rgba(255,255,255,0.1)'
                      : 'transparent',
                  opacity: highlightedIndex === index ? 0.5 : 1,
                }}>
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  },
);

QuillEditor.displayName = 'QuillEditor';
